import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { SisBundleStatus, SisBundleSummary } from "@canvas-sync/shared";
import { api } from "../api/client";
import { FilterBar, type FilterOption } from "../components/FilterBar";
import { SisBundlesTimeline } from "../components/SisBundlesTimeline";
import {
  CANVAS_ENV_OPTIONS,
  EMPTY_FILTERS,
  applyFilters,
  type Accessors,
  type FilterState,
} from "../lib/filters";
import { POLL, usePoll } from "../lib/usePoll";
import { canvasIsSettled } from "../lib/canvasState";

const NON_TERMINAL_BUNDLE: ReadonlySet<SisBundleStatus> = new Set([
  "RECEIVED",
  "VALIDATING",
  "WAITING_FOR_GHOST_DETECTION",
  "BUNDLING",
  "POSTING",
]);

/**
 * A bundle warrants the fast list poll while its lifecycle is non-terminal OR
 * it posted to Canvas but Canvas hasn't settled (importing → imported/failed),
 * so the list surfaces the Canvas outcome promptly instead of waiting for the
 * slow idle poll. (The list reconciles against Canvas server-side.)
 */
function bundleNeedsActivePoll(b: SisBundleSummary): boolean {
  if (NON_TERMINAL_BUNDLE.has(b.status)) return true;
  const posted =
    b.status === "SUBMITTED" ||
    b.status === "SUBMITTED_SIS_ONLY" ||
    b.status === "SUBMITTED_FIX_ONLY";
  return posted && !canvasIsSettled(b.canvasWorkflowState);
}

const STATUS_OPTIONS: FilterOption[] = [
  { value: "RECEIVED", label: "Received" },
  { value: "VALIDATING", label: "Validating" },
  { value: "INCOMPLETE_INPUTS", label: "Incomplete inputs" },
  { value: "INVALID_INPUTS", label: "Invalid inputs" },
  {
    value: "WAITING_FOR_GHOST_DETECTION",
    label: "Waiting for ghost detection",
  },
  { value: "BUNDLING", label: "Bundling" },
  { value: "POSTING", label: "Posting to Canvas" },
  { value: "DRY_RUN_BUILT", label: "Dry run built" },
  { value: "SKIPPED_UNCHANGED", label: "Skipped (unchanged)" },
  {
    value: "SKIPPED_GHOST_DETECTION_TIMEOUT",
    label: "Ghost detection timed out",
  },
  { value: "SKIPPED_NO_FIX", label: "Nothing to send (no ghost fix)" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "SUBMITTED_SIS_ONLY", label: "Submitted (SIS only)" },
  { value: "SUBMITTED_FIX_ONLY", label: "Submitted (ghost fix only)" },
  { value: "FAILED", label: "Failed" },
];

const TRIGGER_OPTIONS: FilterOption[] = [
  { value: "n8n-cron", label: "Scheduled (n8n)" },
  { value: "manual-retrigger", label: "Manual re-trigger" },
];

const BUNDLE_ACCESSORS: Accessors<SisBundleSummary> = {
  status: (b) => b.status,
  trigger: (b) => b.triggeredBy,
  env: (b) => b.env,
  // Bundle "date" is createdAt — there's no separate finishedAt column
  // exposed in the summary, so createdAt drives both date-range filtering
  // and newest/oldest sort. Matches what the timeline buckets on.
  date: (b) => b.createdAt,
  // No meaningful duration for bundles (the per-step phase timings live
  // on the row but aren't summarized). Undefined sorts last.
  duration: () => undefined,
  searchFields: (b) => [
    b.bundleId,
    b.dropId,
    b.sisImportId,
    b.triggeredBy,
    b.status,
    b.fixSkipReason,
    // The list shows BOTH Canvas envs (tagged per row); make them searchable.
    b.env,
  ],
};

export function SisBundlesPage() {
  const [bundles, setBundles] = useState<SisBundleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  usePoll(
    async () => {
      try {
        // Pull a wide page — filtering is client-side via applyFilters so
        // the operator can pivot status/search without round-trips.
        const result = await api.listSisBundles({ limit: 200 });
        setBundles(result.bundles);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    () =>
      bundles && bundles.some(bundleNeedsActivePoll)
        ? POLL.LIST_ACTIVE_MS
        : POLL.IDLE_MS,
  );

  const visible = useMemo(
    () => (bundles ? applyFilters(bundles, filters, BUNDLE_ACCESSORS) : []),
    [bundles, filters],
  );

  // One-shot settings fetch: while ghost-fix-only mode is active this page
  // shows a STANDING warning. Without it, every bundle silently excluding
  // the 5 SIS files looks identical to a healthy full sync at a glance —
  // which is how the mode's effect goes unnoticed for hours.
  const [submissionMode, setSubmissionMode] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled)
          setSubmissionMode(s.sisBundleSubmissionMode ?? "FULL_BUNDLE");
      })
      .catch(() => {
        /* banner simply doesn't render */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section>
      <div className="page-head">
        <h1>SIS bundles</h1>
        <div className="muted">
          {bundles ? `${bundles.length} loaded` : "Loading…"}
        </div>
      </div>

      {submissionMode === "GHOST_FIX_ONLY" && (
        <div className="warn-banner">
          <strong>Ghost-fix-only mode is active.</strong> Hourly bundles send
          only the ghost-fix CSV — the 5 PeopleSoft SIS files are checked and
          archived but <strong>not sent to Canvas</strong>, so course, user, and
          enrollment updates from PeopleSoft are paused while this mode is on.
          Switch back in <Link to="/settings">Settings</Link>.
        </div>
      )}

      {bundles && (
        <FilterBar
          value={filters}
          onChange={setFilters}
          statuses={STATUS_OPTIONS}
          triggers={TRIGGER_OPTIONS}
          envs={CANVAS_ENV_OPTIONS}
          searchPlaceholder="Search by bundle ID, drop ID, Canvas import ID, or skip reason…"
          totalCount={bundles.length}
          filteredCount={visible.length}
        />
      )}

      <div className="card">
        {error && <div className="error">{error}</div>}
        {bundles === null && !error && <div className="muted">Loading…</div>}
        {bundles?.length === 0 && (
          <div className="muted">
            No SIS bundles yet. The bundler creates one each time a{" "}
            <code>_MANIFEST.json</code> lands in{" "}
            <code>
              sis-inputs/{`{env}`}/{`{dropId}`}/
            </code>
            .
          </div>
        )}
        {bundles && bundles.length > 0 && visible.length === 0 && (
          <div className="muted">No bundles match these filters.</div>
        )}
        {visible.length > 0 && <SisBundlesTimeline bundles={visible} />}
      </div>
    </section>
  );
}
